import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  backdrop-filter: blur(12px);
  background: rgba(255, 255, 255, 0.85);
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const BarInner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }
`;

const Logo = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: 1.18rem;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.primary};
`;

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  display: block;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
  }
`;

const LogoText = styled.span`
  line-height: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Links = styled.nav`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledLink = styled(NavLink)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: ${({ theme }) => theme.transitions.default};

  &.active {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownTrigger = styled(NavLink)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  text-decoration: none;

  &.active {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.75rem);
  left: 0;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1rem;
  padding: 0.75rem;
  min-width: 200px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const DropdownItem = styled(NavLink)`
  display: block;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  font-size: 0.9rem;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.primary};
  }

  &.active {
    background: ${({ theme }) => theme.colors.primary}10;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const CTAButton = styled(NavLink)`
  padding: 0.7rem 1.45rem;
  border-radius: 999px;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary}33;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
    background: #f8fafc;
    border-color: ${({ theme }) => theme.colors.primary}55;
  }
`;

const navLinks = [
  { path: '/', label: '홈' },
  { path: '/greeting', label: '인사말' },
  { path: '/schedule', label: '수업시간표' },
  { path: '/location', label: '오시는 길' },
];

const curriculumSubMenu = [
  { path: '/programs/elementary', label: '초등 프로그램' },
  { path: '/programs/middle', label: '중등 프로그램' },
  { path: '/programs/high', label: '고등 프로그램' },
  { path: '/programs/adult', label: '성인 프로그램' },
];

const RNavBar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <Bar>
      <BarInner>
        <Logo to="/">
          <LogoImage src="/logo_394F74.png" alt="ACE CODING" />
          <LogoText>ACE CODING</LogoText>
        </Logo>
        <Links>
          <StyledLink to="/" exact>
            홈
          </StyledLink>
          <StyledLink to="/greeting">
            인사말
          </StyledLink>
          <DropdownContainer
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <DropdownTrigger 
              to="/programs" 
              isActive={(match, location) => {
                return location.pathname.startsWith('/programs');
              }}
            >
              커리큘럼
            </DropdownTrigger>
            <DropdownMenu isOpen={isDropdownOpen}>
              <DropdownItem to="/programs" exact>
                전체 보기
              </DropdownItem>
              {curriculumSubMenu.map((item) => (
                <DropdownItem key={item.path} to={item.path}>
                  {item.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownContainer>
          {navLinks.slice(2).map((item) => (
            <StyledLink key={item.path} to={item.path}>
              {item.label}
            </StyledLink>
          ))}
        </Links>
        <CTAButton to="/join">상담 문의하기</CTAButton>
      </BarInner>
    </Bar>
  );
};

export default RNavBar;

